import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Spin } from "antd";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styled from "styled-components";
import { find, sumBy } from "lodash";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useReactToPrint } from "react-to-print";

import Button from "atoms/Button";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { getManagingOffice, getUserList, getUserInfo } from "redux/selectors";
import { currencyAmount } from "utils/functions";
import currencyMasterData from "common/currencyRateMasterData.json";
import { billingOffice } from "common/Constants";
import userlogo from "../../assets/InsytLogo.svg";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledTable = styled(Table)`
  .ant-table-cell {
    padding: 10px 14px;
  }
  .action-status {
    background-color: #f7f7f8;
  }
  .ant-table-tbody > tr > td,
  .ant-table-thead > tr > th {
    border: 1px solid #dbdbdb;
  }
`;

const StyledButton = styled(Button)`
  background: #00685F !important;
  border-radius: 5px;
  color: #ffffff !important;
   &:hover {
      color: #ffffff !important;
  }
   &:focus {
      color: #ffffff !important;
  }
`;

// ─── Key-Value Table Columns (reused for all summary tables) ─────────────────

const kvColumns = [
  {
    title: "",
    dataIndex: "name",
    key: "name",
    width: "30%",
    className: "action-status",
  },
  {
    title: "",
    dataIndex: "value",
    key: "value",
    width: "70%",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const svgToPngDataUrl = (svgUrl, width = 80, height = 30) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = svgUrl;
  });

// ─── Component ────────────────────────────────────────────────────────────────

function FinancialSummaryMatter() {
  const { id } = useParams();
  const history = useHistory();

  // ── Redux ──────────────────────────────────────────────────────────────────
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const manageoffice = useSelector(getManagingOffice); // kept for future use
  const location = useLocation();
  const matterData = location.state?.matterData;
  // ── Query keys ────────────────────────────────────────────────────────────
  const billKey = ["billing-by-matter", id];
  const billUrl = `/insyts/billing?matterid=${id}&lastEvaluatedKey=0`;
  // const tsKey = ["timesheet-by-matter", id];
  // const tsUrl = `/insyts/timesheet?matterid=${id}`;

  // ── Remote data ───────────────────────────────────────────────────────────
  const { data: billRes } = useFetch(billKey, billUrl, { enabled: !!id });
  // const { data: tsRes } = useFetch(tsKey, tsUrl, { enabled: !!id });

  const fmtUSD = (val) =>
		val !== undefined && val !== null && val !== ""
			? `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
			: `$0.00`;

  // ── Derived data ──────────────────────────────────────────────────────────
  const engagementData = matterData || {};
  console.log("engagementData", engagementData);

  const billingData = useMemo(
    () =>
      (billRes?.response?.items ?? []).filter(
        (v) =>
          v?.statusname === "Approved"
      ),
    [billRes]
  );

  // const timeSheetData = useMemo(
  //   () => tsRes?.response?.response ?? [],
  //   [tsRes]
  // );

  const { key: claimsKey, url: claimsUrl } =
    queryKeys.getClaims(`?matterid=${id}&clientId=${engagementData?.clientId}&matterclaims=true`);

  const { data: claimsData } = useFetch(claimsKey, claimsUrl, {
    enabled: !!id && !!engagementData?.clientId,
  });

  const { key: actualSummaryKey, url: actualSummaryUrl } =
      queryKeys.getActualSummaryToEngagementId(id);
  
    const { data: actualSummaryData } = useFetch(actualSummaryKey, actualSummaryUrl, {
      enabled: !!id,
    });
  
    const actualSummaryTimeSheetData = useMemo(() => {
      return actualSummaryData?.response?.data ?? [];
    }, [actualSummaryData]);

  // ── Rates & conversion ────────────────────────────────────────────────────
  const conversionRate = Number(
    engagementData?.financial_resourcePlan?.conversionRate
  );

  const convertionRate = currencyMasterData.find(
    (c) => c.TBD_DESC === engagementData?.financial_resourcePlan?.currency
  )?.TBD_ABBR;

  const actualBillingAmount =
	(billingData
		?.filter((item) => item.statusname === "Approved")
		.reduce((sum, b) => {
		const fee = Number(b.billingfee) || 0;
		const tax = b.vat?.toUpperCase() === "VAT" ? (Number(b.tax) / 100) : 1;
		return sum + fee * tax;
		}, 0) ?? 0) * (Number(conversionRate) || 1);

  console.log("actualBillingAmount", actualBillingAmount);

  const approvedDisbursementSubcontract = billingData
		?.filter((item) => item.statusname === "Approved")
		?.reduce(
			(acc, item) => {
				item.claims.forEach((claim) => {
					const amount = parseFloat(claim.amount) || 0;
					if (claim.type.toLowerCase() === "subcontract") {
						acc.subcontractTotal += amount;
					} else {
						acc.disbursementTotal += amount;
					}
				});
				return acc;
			},
			{ subcontractTotal: 0, disbursementTotal: 0 }
		);

	const actualBillingDisbursement =
		Number(approvedDisbursementSubcontract?.disbursementTotal) * Number(conversionRate) || 0;

	const actualBillingSubcontract =
		Number(approvedDisbursementSubcontract?.subcontractTotal) * Number(conversionRate) || 0;

   const employeeLists = useMemo(
    () =>
      Array.from(
        new Map(
          (matterData?.employeeList ?? [])
            .filter((item) => item?.userId !== null)
            .map((item) => [item.resourceId, item])
        ).values()
      ),
    [engagementData]
  );
  console.log("actualSummaryTimeSheetData", actualSummaryTimeSheetData);
	const matched = [];
	actualSummaryTimeSheetData?.forEach((ts) => {
		ts?.actualTimesheet
			?.filter((entry) => entry.status === "approved")
			?.forEach((entry) => {
				const emp = employeeLists.find(
					(e) => ts?.userId === e?.userId && Number(entry?.resourceId) === e?.resourceId
				);
				const alreadyMatched = matched?.find(
					(m) => m?.userId === ts?.userId && m?.resourceId === entry?.resourceId
				);
				if (emp && !alreadyMatched) {
					matched?.push({
						userId: ts?.userId,
						resourceId: entry?.resourceId,
						approvedHours: entry?.sum,
					});
				}
			});
	});

	const actualChargeout = matched?.reduce((total, item) => {
		const emp = employeeLists?.find(
			(e) => e?.userId === item?.userId && Number(item?.resourceId) === e?.resourceId
		);
		const user = userList?.find(
			(user) => user?.userid?.toLowerCase() === item?.userId?.toLowerCase()
		);
		const rate = Number(emp?.resourceExtRate ?? user?.chargeoutrate ?? 0);
		const tsEntry = actualSummaryTimeSheetData?.find((ts) => ts?.userId === item?.userId);
		const approvedHours =
			tsEntry?.actualTimesheet
				?.filter(
					(ts) =>
						ts?.resourceId.toString() === item?.resourceId.toString() &&
						ts?.status === "approved"
				)
				?.reduce((acc, ts) => acc + (Number(ts?.sum) || 0), 0) || 0;
		return total + approvedHours * rate;
	}, 0);

  console.log("actualChargeout",actualChargeout);

  const billedClaimIds = new Set(
		billingData?.filter((item) => item.statusname === "Approved")?.flatMap(bill => bill.claims.map(claim => claim.claimsId))
	);

	const filteredClaimsData = claimsData?.response?.data?.filter(claim => !billedClaimIds?.has(claim.claimsid));

	const subcontractTotal = filteredClaimsData
		?.filter(claim => claim.type.toLowerCase() === "subcontract")
		?.reduce((sum, claim) => sum + parseFloat(claim.totalamount || 0), 0);

	const disbursementTotal = filteredClaimsData
		?.filter(claim => claim.type.toLowerCase() !== "subcontract")
		?.reduce((sum, claim) => sum + parseFloat(claim.totalamount || 0), 0);

	const actualDisbursement =
		Number(disbursementTotal) * Number(conversionRate) || 0;

	const actualSubcontract =
		Number(subcontractTotal) * Number(conversionRate) || 0;

  const [profitLoss, setProfitLoss] = useState(0);

  useEffect(() => {
    setProfitLoss(
      actualBillingAmount +
      actualBillingDisbursement + 
	    actualBillingSubcontract -
      actualChargeout -
      (actualDisbursement + actualSubcontract)
    );
  }, [
    actualBillingAmount,
    actualBillingDisbursement,
    actualBillingSubcontract,
    actualChargeout,
    actualDisbursement,
    actualSubcontract
  ]);

  // ── Budget chargeout ───────────────────────────────────────────────────────
  const [budjetChargeout, setBudjetChargeout] = useState(0);
  useEffect(() => {
    const mergedList = (engagementData?.employeeList ?? []).map((employee) => {
      const user = employeeLists.find(
        (u) =>
          u?.userId === employee?.userId &&
          Number(u.resourceId) === Number(employee.resourceId)
      );
      return {
        ...employee,
        chargeoutrate: user ? user.resourceExtRate.toString() : null,
      };
    });

    const total = mergedList
      .filter((item) => item.personDay !== undefined && !isNaN(item.personDay))
      .reduce((acc, item) => {
        const rate = Number(item.chargeoutrate);
        return !isNaN(rate) ? acc + rate * item.personDay * 8 : acc;
      }, 0);

    setBudjetChargeout(total);
  }, [engagementData, employeeLists]);

  const billingAmountNum = Number(
    String(
      engagementData?.financial_resourcePlan?.billingAmount || "0"
    ).replace(/,/g, "")
  );

  const conversionRateNum = Number(
    String(
      engagementData?.financial_resourcePlan?.conversionRate ?? 1
    ).replace(/,/g, "")
  );

  const subcontractorTotal = (engagementData?.subcontractList ?? [])?.reduce((sum, item) => sum + item.amount * Number(item.currencyrate), 0) || 0;

  const budgetedProfit =
    billingAmountNum * conversionRateNum -
    (Number(budjetChargeout) + Number(subcontractorTotal));

  const getUserNameByEmail = (email) => {
    if (!email) return "-";

    const user = userList?.find(
      (u) => u.userid?.toLowerCase() === email?.toLowerCase()
    );

    return user?.fullname || email; // fallback to email if not found
  };
  // ── Table data arrays ──────────────────────────────────────────────────────
  const informationRows = [
    { name: "Matter Title", value: engagementData?.jobTitle || "-" },
    { name: "Managing Office", value: engagementData?.location || "-" },
    {
      name: "Lead Attorney",
      value: getUserNameByEmail(engagementData?.leadAttorney),
    },
    {
      name: "Referring Attorney",
      value: getUserNameByEmail(engagementData?.referringAttorney),
    },
    { name: "Client", value: engagementData?.clientName || "-" },
    { name: "Practice Area", value: engagementData?.practiceArea || "-" },
    { name: "Plan Start Date", value: engagementData?.planStartDate || "-" },
    { name: "Plan End Date", value: engagementData?.planEndDate || "-" },
    { name: "Client FY Month", value: engagementData?.clientFyMonth || "-" },
  ];
  
  const plannedFinancialRows = [
    {
      name: "Planned Billing",
      value: fmtUSD(billingAmountNum * conversionRateNum),
    },
    {
      name: "Planned Chargeout",
      value: fmtUSD(budjetChargeout),
    },
    {
      name: "Subcontract (Claims)",
      value: fmtUSD(subcontractorTotal),
    },
    {
      name: "Profit",
      value: fmtUSD(budgetedProfit),
    },
    {
      name: "PFA USD",
      value:
        budgetedProfit < 0
          ? fmtUSD(Math.abs(budgetedProfit))
          : "$0",
    },
  ];

  const actualFinancialRows = [
    {
      name: "Actual Billing",
      value: fmtUSD(actualBillingAmount),
    },
    {
      name: "Billing Disbursement",
      value: fmtUSD(actualBillingDisbursement),
    },
    {
      name: "Billing Subcontract",
      value: fmtUSD(actualBillingSubcontract),
    },
    {
      name: "Chargeout",
      value: fmtUSD(actualChargeout),
    },
    {
      name: "Disbursement (Claims)",
      value: fmtUSD(actualDisbursement),
    },
    {
      name: "Subcontract (Claims)",
      value: fmtUSD(actualSubcontract),
    },
    {
      name: "Profit/Loss",
      value: fmtUSD(profitLoss || 0),
    },
  ];

  const plannedManhours = sumBy(
    engagementData?.employeeList,
    (e) => Number(e?.personDay || 0)
  ) * 8;

  const actualManhours = actualSummaryTimeSheetData.reduce((total, entry) => {
    const hours =
      entry.actualTimesheet
        ?.filter((ts) => ts.status === "approved")
        ?.reduce((sum, ts) => sum + (Number(ts.sum) || 0), 0) || 0;
    return total + hours;
  }, 0);

  const timesheetRows = [
    { name: "Planned Manhours", value: Number(plannedManhours).toFixed(2) },
    { name: "Actual Manhours", value: Number(actualManhours).toFixed(2) },
    {
      name: "Variance",
      value: (Number(actualManhours) - Number(plannedManhours || 0)).toFixed(2),
    },
  ];

  // ── Employee detail table columns ─────────────────────────────────────────
  const employeeColumns = [
    {
      title: "S/N",
      dataIndex: "id",
      key: "id",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    {
      title: "Role",
      dataIndex: "projectRole",
      key: "projectRole",
    },
    {
      title: "Planned Manhours",
      dataIndex: "personDay",
      key: "personDay",
      align: "right",
      render: (val) => (val ? Number(val) * 8 : 0),
    },
    {
      title: "Actual Manhours",
      dataIndex: "acutalmanhours",
      key: "acutalmanhours",
      align: "right",
      render: (_, record) => {
        let totalHours = 0;
        actualSummaryTimeSheetData.forEach((entry) => {
          if (entry.userId === record.userId) {
            const filtered = entry.actualTimesheet.filter(
              (ts) =>
                ts.resourceId.toString() === record.resourceId.toString() &&
                ts.status === "approved"
            );
            totalHours += filtered.reduce(
              (sum, ts) => sum + (Number(ts.sum) || 0),
              0
            );
          }
        });
        return totalHours;
      },
    },
    {
      title: "Rate (USD)",
      dataIndex: "resourceExtRate",
      key: "resourceExtRate",
      align: "right",
      render: (_, record) => `$${record?.resourceExtRate ?? 0}`,
    },
    {
      title: "Planned Chargeout Amount (USD)",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (_, record) =>
        currencyAmount(
          "$",
          Number(record?.resourceExtRate ?? 0) *
          Number(record?.personDay ?? 0) *
          8
        ),
    },
    {
      title: "Actual total Chargeout Amount (USD)",
      key: "actualChargeout",
      align: "right",
      render: (_, record) => {
        let actualHours = 0;
        actualSummaryTimeSheetData.forEach((entry) => {
          if (entry.userId === record.userId) {
            const filtered = entry.actualTimesheet.filter(
              (ts) =>
                ts.resourceId.toString() === record.resourceId.toString() &&
                ts.status === "approved"
            );
            actualHours += filtered.reduce(
              (sum, ts) => sum + (Number(ts.sum) || 0),
              0
            );
          }
        });
        return currencyAmount(
          "$",
          Number(record?.resourceExtRate ?? 0) * actualHours
        );
      },
    },
    // {
    //   title: "Claim (USD)",
    //   dataIndex: "claims",
    //   key: "claims",
    //   align: "right",
    //   render: (_, record) => {
    //     const foundItem =
    //       timeSheetData?.find(
    //         (item) =>
    //           Array.isArray(item?.claims) &&
    //           item?.claims?.some(
    //             (claim) =>
    //               claim?.resourceId === String(record?.resourceId) &&
    //               item?.employeeId === record?.userId
    //           )
    //       ) || null;

    //     const totalClaims =
    //       foundItem?.claims
    //         ?.filter(
    //           (claim) => claim?.resourceId === String(record?.resourceId)
    //         )
    //         ?.reduce((sum, claim) => {
    //           const amount = Number(claim?.amount) || 0;
    //           return (
    //             sum +
    //             (claim?.type === "Subcontract"
    //               ? amount
    //               : amount * (Number(convertionRate) || 1))
    //           );
    //         }, 0) || 0;

    //     return totalClaims ? currencyAmount("$", totalClaims) : "-";
    //   },
    // },
  ];

  // ── Employee table summary row ─────────────────────────────────────────────
  const employeeSummary = (pageData) => {
    let totalPlannedHours = 0;
    let totalActualHours = 0;
    let totalPlannedAmt = 0;
    let totalActualAmt = 0;
    let totalClaims = 0;

    pageData.forEach((record) => {
      const plannedHours = Number(record?.personDay || 0) * 8;
      totalPlannedHours += plannedHours;

      let actualHours = 0;
      actualSummaryTimeSheetData.forEach((entry) => {
        if (entry.userId === record.userId) {
          const approved = entry.actualTimesheet.filter(
            (ts) =>
              ts.resourceId.toString() === record.resourceId.toString() &&
              ts.status === "approved"
          );
          actualHours += approved.reduce(
            (sum, ts) => sum + (Number(ts.sum) || 0),
            0
          );
        }
      });
      totalActualHours += actualHours;

      const rate = Number(record?.resourceExtRate || 0);
      totalPlannedAmt += rate * plannedHours;
      totalActualAmt += rate * actualHours;

      // const foundItem =
      //   timeSheetData?.find(
      //     (item) =>
      //       Array.isArray(item?.claims) &&
      //       item?.claims?.some(
      //         (claim) =>
      //           claim?.resourceId === String(record?.resourceId) &&
      //           item?.employeeId === record?.userId
      //       )
      //   ) || null;

      // totalClaims +=
      //   foundItem?.claims
      //     ?.filter(
      //       (claim) =>
      //         claim?.resourceId?.toString() === record?.resourceId?.toString()
      //     )
      //     ?.reduce(
      //       (sum, claim) =>
      //         sum +
      //         (claim?.type === "Subcontract"
      //           ? Number(claim?.amount)
      //           : Number(claim?.amount) * (Number(convertionRate) || 1) || 0),
      //       0
      //     ) || 0;
    });

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3}><b>Total</b></Table.Summary.Cell>
        <Table.Summary.Cell index={3}><b>{totalPlannedHours}</b></Table.Summary.Cell>
        <Table.Summary.Cell index={4}><b>{totalActualHours}</b></Table.Summary.Cell>
        <Table.Summary.Cell index={5} />
        <Table.Summary.Cell index={6}><b>{currencyAmount("$", totalPlannedAmt)}</b></Table.Summary.Cell>
        <Table.Summary.Cell index={7}><b>{currencyAmount("$", totalActualAmt)}</b></Table.Summary.Cell>
        {/* <Table.Summary.Cell index={8}><b>{currencyAmount("$", totalClaims)}</b></Table.Summary.Cell> */}
      </Table.Summary.Row>
    );
  };

  // ── PDF generation ────────────────────────────────────────────────────────
  const mainContentRef = useRef();
  const lastTableRef = useRef();
  const logoCacheRef = useRef(null);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const generatePdf = async () => {
    if (!mainContentRef.current || !lastTableRef.current) return;
    setIsPrintMode(true);
    setLoadingPdf(true);

    try {
      const pdf = new jsPDF("p", "pt", "a4");
      const margin = 10;
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const headerHeight = 60;
      const imgWidth = pdfWidth - margin * 2;
      const pageContentH = pdfHeight - margin * 2 - headerHeight;

      if (!logoCacheRef.current) {
        logoCacheRef.current = await svgToPngDataUrl(userlogo, 80, 30);
      }
      const logoPng = logoCacheRef.current;

      const printedBy = `Printed By: ${userInfo.userid}`;
      const printedDate = `${moment().format("dddd, DD MMMM YYYY")} - ${moment().format("HH:mm:ss")}`;

      const addHeader = () => {
        const cx = pdfWidth / 2;
        pdf.addImage(logoPng, "PNG", cx - 40, margin, 80, 30);
        // Increased from margin + 45 to margin + 55 to add space
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text("Financial Summary", cx, margin + 55, { align: "center" });
      };

      const addFooter = () => {
        const footerY = pdfHeight - margin + 10;
        pdf.setFontSize(10);
        pdf.text(printedBy, margin, footerY);
        pdf.text(printedDate, pdfWidth - margin, footerY, { align: "right" });
      };

      // Render main summary section
      const canvasMain = await html2canvas(mainContentRef.current, { scale: 1.5, useCORS: true });
      const imgMainH = (canvasMain.height * imgWidth) / canvasMain.width;
      const imgMainData = canvasMain.toDataURL("image/jpeg", 0.9);
      let mainHeightLeft = imgMainH;

      addHeader();
      pdf.addImage(imgMainData, "JPEG", margin, margin + headerHeight, imgWidth, imgMainH);
      addFooter();
      mainHeightLeft -= pageContentH;

      while (mainHeightLeft > 0) {
        pdf.addPage();
        addHeader();
        pdf.addImage(
          imgMainData,
          "JPEG",
          margin,
          margin + headerHeight - (imgMainH - mainHeightLeft),
          imgWidth,
          imgMainH
        );
        addFooter();
        mainHeightLeft -= pageContentH;
      }

      // Render employee detail table
      pdf.addPage();
      addHeader();
      const canvasLast = await html2canvas(lastTableRef.current, { scale: 1.5, useCORS: true });
      const imgLastH = (canvasLast.height * imgWidth) / canvasLast.width;
      const imgLastData = canvasLast.toDataURL("image/jpeg", 0.9);
      let lastHeightLeft = imgLastH;

      pdf.addImage(imgLastData, "JPEG", margin, margin + headerHeight, imgWidth, imgLastH);
      addFooter();
      lastHeightLeft -= pageContentH;

      while (lastHeightLeft > 0) {
        pdf.addPage();
        addHeader();
        pdf.addImage(
          imgLastData,
          "JPEG",
          margin,
          margin + headerHeight - (imgLastH - lastHeightLeft),
          imgWidth,
          imgLastH
        );
        addFooter();
        lastHeightLeft -= pageContentH;
      }

      pdf.save("Financial Summary.pdf");
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setLoadingPdf(false);
      setIsPrintMode(false);
    }
  };

  // ── Breadcrumbs ───────────────────────────────────────────────────────────
  const breadStyle = { color: "#636980", cursor: "pointer" };
  const breadcrumbs = [
    <label key="1" style={breadStyle} onClick={() => history.goBack()}>
      {engagementData?.information?.jobTitle || "Matter"}
    </label>,
    <Typography key="2" color="text.primary">
      Financial Summary
    </Typography>,
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        {breadcrumbs}
      </Breadcrumbs>

      {/* Print button */}
      <div className="text-end" style={{ marginBottom: 12 }}>
        <StyledButton
          loading={loadingPdf}
          onClick={generatePdf}
        >
          Print
        </StyledButton>
      </div>

      <Spin spinning={loadingPdf} size="large">
        {/* ── Summary sections ── */}
        <div ref={mainContentRef} className="px-1">

          <h6>Information</h6>

          <StyledTable
            showHeader={false}
            columns={kvColumns}
            dataSource={informationRows}
            pagination={false}
          />

          <br />
          <h6>Financial (USD)</h6>

          <h6>Planned:</h6>
          <StyledTable
            showHeader={false}
            columns={kvColumns}
            dataSource={plannedFinancialRows}
            pagination={false}
          />

          <br />
          <h6>Actual:</h6>
          <StyledTable
            showHeader={false}
            columns={kvColumns}
            dataSource={actualFinancialRows}
            pagination={false}
          />

          <br />
          <h6>Timesheet</h6>
          <StyledTable
            showHeader={false}
            columns={kvColumns}
            dataSource={timesheetRows}
            pagination={false}
          />

          <br />
        </div>

        {/* ── Employee detail table ── */}
        <div ref={lastTableRef} className="px-1">
          <StyledTable
            columns={employeeColumns}
            pagination={false}
            dataSource={employeeLists}
            summary={employeeSummary}
          />
        </div>
      </Spin>
    </div>
  );
}

export default FinancialSummaryMatter;